import React, { useState, useMemo } from 'react';
import {
  Sparkles, Wand2, TrendingUp, AlertTriangle, Lightbulb, FileBarChart, RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { getInsights, generateReportFromPrompt, AI_PROMPT_SUGGESTIONS } from '../ai/aiAssistant';

const INSIGHT_META = {
  summary: { icon: FileBarChart, cls: 'text-slate-600 bg-slate-100' },
  trend: { icon: TrendingUp, cls: 'text-emerald-600 bg-emerald-50' },
  anomaly: { icon: AlertTriangle, cls: 'text-amber-600 bg-amber-50' },
  suggestion: { icon: Lightbulb, cls: 'text-indigo-600 bg-indigo-50' },
};

const AiPanel = ({ result, config, fieldMap, onApplyConfig }) => {
  const [prompt, setPrompt] = useState('');
  const [seed, setSeed] = useState(0);

  const insights = useMemo(
    () => getInsights(result, config, fieldMap),
    [result, config, fieldMap, seed],
  );

  const handleGenerate = () => {
    if (!prompt.trim()) { toast.error('Describe what you want to see'); return; }
    const partial = generateReportFromPrompt(prompt);
    onApplyConfig(partial);
    toast.success('AI rebuilt the report from your prompt');
  };

  return (
    <div className="flex flex-col h-full">
      <div className="px-3 py-3 border-b border-slate-200">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
          <Sparkles className="h-4 w-4 text-indigo-600" /> AI Assistant
        </h3>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-4">
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-3 border border-indigo-100">
          <div className="text-xs font-semibold text-indigo-900 mb-1.5">Generate from a prompt</div>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="e.g. Pipeline value by stage as a funnel"
            className="w-full h-16 text-xs rounded-lg border border-indigo-200 p-2 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />
          <div className="flex flex-wrap gap-1 mt-1.5">
            {AI_PROMPT_SUGGESTIONS.slice(0, 3).map((s) => (
              <button key={s} onClick={() => setPrompt(s)}
                className="text-[10px] bg-white/70 hover:bg-white text-indigo-700 px-1.5 py-0.5 rounded-full border border-indigo-200">
                {s}
              </button>
            ))}
          </div>
          <Button size="sm" onClick={handleGenerate} className="w-full mt-2 h-7 bg-indigo-600 hover:bg-indigo-700">
            <Wand2 className="h-3.5 w-3.5 mr-1.5" /> Rebuild Report
          </Button>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-slate-400 uppercase">Live Insights</span>
            <button onClick={() => setSeed((s) => s + 1)} className="text-slate-400 hover:text-indigo-600" title="Refresh">
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="space-y-2">
            {insights.map((ins) => {
              const meta = INSIGHT_META[ins.type] || INSIGHT_META.summary;
              const Icon = meta.icon;
              return (
                <div key={ins.id} className="border border-slate-200 rounded-lg p-2.5">
                  <div className="flex items-start gap-2">
                    <div className={`p-1.5 rounded-lg ${meta.cls}`}>
                      <Icon className="h-3.5 w-3.5" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-slate-800">{ins.title}</div>
                      <div className="text-xs text-slate-600 mt-0.5 leading-snug">{ins.text}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiPanel;
